/**
 * CR-7781: the haulier's Confirm Load — the second confirmation on a call-off.
 *
 * A footer action, beside the other call-off workflow buttons (Send, Confirm,
 * Create ASN), with the same short hint after it. Shown only to the assigned
 * haulier (`can_confirm_load`), and disabled until the three values the
 * confirmation rests on are saved: haulage cost, pickup date and agreed
 * delivery date. The lorry number is not one of them — it is only known on
 * the day. The server enforces the same rule; the disabled button and its
 * hint are so the haulier can see what is still missing.
 *
 * @link CallOffDetailModalFooter for the host.
 * @link CallOffHaulagePanel where the values are entered.
 */
import { useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { Button } from '../../ui/button';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** The saved values Confirm Load needs, and how each is named when missing. */
const REQUIRED: ReadonlyArray<{ field: 'haulage_cost' | 'haulier_pickup_date' | 'haulier_agreed_delivery_date'; label: string }> = [
  { field: 'haulage_cost', label: 'haulage cost' },
  { field: 'haulier_pickup_date', label: 'pickup date' },
  { field: 'haulier_agreed_delivery_date', label: 'agreed delivery date' },
];

/**
 * A list read as a sentence: "a", "a and b", "a, b and c".
 *
 * @param items - the words to join
 * @returns the joined phrase
 */
function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

interface CallOffConfirmLoadProps {
  callOff: CallOff;
  /** The Haulage block has unsaved edits — confirming would use the old values. */
  haulageDirty: boolean;
  /** Reload the call-off (and the list behind it) after confirming. */
  onConfirmed: () => void;
}

export default function CallOffConfirmLoad({ callOff, haulageDirty, onConfirmed }: CallOffConfirmLoadProps) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (callOff.can_confirm_load !== true) return null;

  const missing = REQUIRED.filter(({ field }) => !callOff[field]).map(({ label }) => label);
  const blockedBy = missing.length > 0
    ? `Enter and save the ${joinAnd(missing)} in the Haulage section to confirm the load.`
    : haulageDirty
      ? 'Save the haulage details before confirming the load.'
      : null;

  const confirm = async () => {
    setConfirming(true);
    setError(null);
    try {
      await callOffAPI.confirmLoad(callOff.id);
      toast.success('Load confirmed — Logistics Confirmed');
      onConfirmed();
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to confirm the load');
      // Kept on screen as well as toasted: a refusal such as "locked because
      // the load is in transit" must not vanish with the toast.
      setError(message);
      toast.error(message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <Button size="sm" className="gap-1.5" onClick={confirm} disabled={confirming || blockedBy !== null}>
        {confirming
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <ClipboardCheck className="h-3.5 w-3.5" />}
        Confirm Load
      </Button>
      {error ? (
        <span role="alert" className="text-xs text-red-600">{error}</span>
      ) : (
        <span className="text-xs text-muted-foreground">
          {blockedBy ?? 'Confirm you have booked this load. The lorry number can be added later.'}
        </span>
      )}
    </>
  );
}
